/**
 * Separate account verification for the Ailaunch flow.
 * Can render as a modal (default) or inline on the page (inline={true}).
 */
import {
  Dialog,
  Typography,
  IconButton,
  Box,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import React, { useState, useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { useForm, Controller } from 'react-hook-form';
import AuthService from '../services/auth.service';
import { config } from '../config';
import { toast } from 'react-toastify';

function getAilaunchTryAiFlowUrl(): string {
  const baseUrl =
    config.easywebinar_v2_url && config.easywebinar_v2_url !== 'N/A'
      ? config.easywebinar_v2_url
      : window.location.origin;
  return `${baseUrl.replace(/\/$/, '')}/ai-webinar/try-ai-flow`;
}

export interface AccountVerificationAilaunchProps {
  open?: boolean;
  onProceed: () => void;
  onClose: () => void;
  phoneNumber: string;
  email: string;
  password?: string;
  companyId: string;
  companyName: string;
  infLeadSquaredId: string;
  /** When true, render on the page instead of in a modal */
  inline?: boolean;
}

const AccountVerificationAilaunch: React.FC<AccountVerificationAilaunchProps> = ({
  open = true,
  onClose,
  onProceed,
  email,
  password = '',
  companyId,
  companyName,
  infLeadSquaredId,
  inline = false,
}) => {
  const [timeLeft, setTimeLeft] = useState(20);
  const [isLoading, setIsLoading] = useState(false);

  const { control, watch, setValue } = useForm({
    defaultValues: {
      otp: ['', '', '', '', '', ''],
    },
  });

  const otpValues: string[] = watch('otp') || ['', '', '', '', '', ''];

  const isVisible = inline || open;

  useEffect(() => {
    if (!isVisible) {
      setTimeLeft(20);
      return;
    }
    const timer = setInterval(() => {
      setTimeLeft((prevTime) => {
        if (prevTime <= 0) {
          clearInterval(timer);
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [isVisible]);

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      if (otpValues && !otpValues[index] && index > 0) {
        const prevInput = document.querySelector(`input[name="otp.${index - 1}"]`) as HTMLInputElement;
        if (prevInput) prevInput.focus();
      }
    }
  };

  const handlePaste = (event: React.ClipboardEvent) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text');
    const pastedNumbers = pastedData.replace(/[^\d]/g, '').slice(0, 6);
    if (pastedNumbers && otpValues) {
      const newOtp = [...otpValues];
      pastedNumbers.split('').forEach((digit, index) => {
        if (index < 6) newOtp[index] = digit;
      });
      newOtp.forEach((value, index) => {
        setValue(`otp.${index}`, value);
      });
    }
  };

  const handleResendCode = async () => {
    if (timeLeft === 0) {
      try {
        await AuthService.resendConfirmationCode(email);
        toast.success('Code resent successfully');
        setTimeLeft(20);
        const timer = setInterval(() => {
          setTimeLeft((prevTime) => {
            if (prevTime <= 0) {
              clearInterval(timer);
              return 0;
            }
            return prevTime - 1;
          });
        }, 1000);
      } catch (error: any) {
        toast.error('Failed to resend code');
      }
    }
  };

  const handleClose = () => {
    [0, 1, 2, 3, 4, 5].forEach((index) => setValue(`otp.${index}`, ''));
    onClose();
  };

  const handleProceed = async () => {
    const confirmationCode = otpValues?.join('') || '';
    if (confirmationCode.length !== 6) {
      toast.error('Please enter the complete 6-digit code');
      return;
    }
    try {
      setIsLoading(true);
      await AuthService.confirmSignup({
        email,
        confirmationCode,
        password: password || '',
        infLeadSquaredId,
        companyId,
        companyName,
      });
      toast.success('Account verified successfully.');
      onProceed();
      window.location.href = getAilaunchTryAiFlowUrl();
    } catch (error: any) {
      toast.error(error?.message ?? 'Verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  const codeComplete = otpValues.join('').length === 6;

  const otpInputSx = {
    width: 48,
    '& .MuiOutlinedInput-root': {
      width: 48,
      height: 48,
      borderRadius: 2,
      bgcolor: '#fff',
      '& fieldset': {
        borderColor: '#CFC4E2',
        borderWidth: 1,
      },
      '&:hover fieldset': { borderColor: '#9B51E0' },
      '&.Mui-focused fieldset': {
        borderColor: '#6F42C1',
        borderWidth: 1,
      },
      '& input': {
        padding: 0,
        textAlign: 'center',
        fontSize: '1.125rem',
        fontWeight: 600,
        color: '#1A1A1A',
      },
    },
    '& .MuiOutlinedInput-input': { height: '100%' },
  };

  const verificationContent = (
    <>
      {!inline && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px 0', position: 'relative' }}>
          <Typography sx={{ fontSize: { xs: '1.75rem', sm: '2rem' }, fontWeight: 700, color: '#1A1A1A' }}>
            Verify your email
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: '#666666',
              position: 'absolute',
              top: '12px',
              right: '16px',
              '&:hover': { color: '#1A1A1A', bgcolor: 'rgba(0,0,0,0.05)' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      )}
      <Box sx={{ padding: inline ? 0 : '24px 24px 20px', pt: inline ? 0 : 0 }}>
        {!inline && (
          <Typography sx={{ color: '#666666', fontSize: '1rem', textAlign: 'center', mb: 2.5 }}>
            We sent a code to {email}
          </Typography>
        )}
        <Box display="flex" gap={1.5} justifyContent="center" mb={2} flexWrap="wrap">
          {[0, 1, 2, 3, 4, 5].map((index) => (
            <Controller
              key={index}
              name={`otp.${index}`}
              control={control}
              rules={{ pattern: /^[0-9]*$/ }}
              render={({ field }) => (
                <TextField
                  {...field}
                  name={`otp.${index}`}
                  inputProps={{ maxLength: 1, inputMode: 'numeric', pattern: '[0-9]*', 'aria-label': `Digit ${index + 1}` }}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!/^\d*$/.test(value)) return;
                    field.onChange(value.slice(-1));
                    if (value && index < 5) {
                      const nextInput = document.querySelector(`input[name="otp.${index + 1}"]`) as HTMLInputElement;
                      if (nextInput) nextInput.focus();
                    }
                  }}
                  onKeyDown={(e) => handleKeyDown(index, e as React.KeyboardEvent<HTMLInputElement>)}
                  onPaste={handlePaste}
                  sx={otpInputSx}
                />
              )}
            />
          ))}
        </Box>
        {!inline && (
          <Typography
            sx={{
              color: '#6F42C1',
              fontSize: '0.9375rem',
              textAlign: 'center',
              fontWeight: 500,
              textDecoration: 'underline',
              cursor: timeLeft === 0 ? 'pointer' : 'default',
              '&:hover': timeLeft === 0 ? { color: '#5a32a3' } : {},
            }}
            onClick={handleResendCode}
          >
            {timeLeft === 0
              ? 'Resend code'
              : `Resend code in ${String(Math.floor(timeLeft / 60)).padStart(2, '0')}:${String(timeLeft % 60).padStart(2, '0')} sec`}
          </Typography>
        )}
      </Box>
      {!inline && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: '20px 24px 24px', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              textTransform: 'none',
              px: 3,
              borderRadius: 2,
              borderColor: '#CFC4E2',
              color: '#666666',
              height: '47px',
              fontWeight: 600,
              '&:hover': { borderColor: '#6F42C1', color: '#6F42C1', bgcolor: 'rgba(111,66,193,0.05)' },
            }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={isLoading || !codeComplete}
            sx={{
              textTransform: 'none',
              borderRadius: 2,
              px: 3,
              height: '47px',
              fontSize: '1rem',
              fontWeight: 600,
              ...(isLoading || !codeComplete
                ? { bgcolor: '#E0E0E0', color: '#A0A0A0', cursor: 'not-allowed' }
                : { bgcolor: '#6F42C1', color: '#fff', '&:hover': { bgcolor: '#5a32a3' } }
              )
            }}
            onClick={handleProceed}
          >
            {isLoading ? 'Verifying...' : 'Verify & Continue'}
          </Button>
        </Box>
      )}
    </>
  );

  if (inline) {
    return (
      <Box sx={{ maxWidth: 480, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', flex: '0 0 auto' }}>
        <Box sx={{ textAlign: 'center', width: '100%', mb: 1.5 }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: 'inherit',
              fontSize: { xs: '1.75rem', sm: '2rem' },
              fontWeight: 700,
              color: '#1A1A1A',
              marginBottom: 0.5,
              lineHeight: 1.2,
            }}
          >
            Verify your email
          </Typography>
          <Typography
            sx={{
              fontFamily: 'inherit',
              fontSize: '1rem',
              fontWeight: 400,
              color: '#666666',
              lineHeight: 1.3,
            }}
          >
            We sent a code to {email}
          </Typography>
        </Box>
        <Box
          component="form"
          onSubmit={(e: React.FormEvent) => { e.preventDefault(); handleProceed(); }}
          sx={{
            maxWidth: 480,
            width: '100%',
            p: 3,
            boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
            borderRadius: '24px',
            bgcolor: '#ffffff',
          }}
        >
          {verificationContent}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isLoading || !codeComplete}
            sx={{
              py: 1.25,
              borderRadius: 2,
              fontSize: '1rem',
              fontWeight: 600,
              textTransform: 'none',
              ...(isLoading || !codeComplete
                ? { bgcolor: '#E0E0E0', color: '#A0A0A0', cursor: 'not-allowed' }
                : { bgcolor: '#6F42C1', color: '#fff', '&:hover': { bgcolor: '#5a32a3' } }
              )
            }}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Verify & Continue'}
          </Button>
          <Typography
            component="button"
            type="button"
            onClick={handleResendCode}
            sx={{
              display: 'block',
              width: '100%',
              mt: 1.5,
              textAlign: 'center',
              fontSize: '0.9375rem',
              fontWeight: 500,
              color: '#6F42C1',
              textDecoration: 'underline',
              background: 'none',
              border: 'none',
              cursor: timeLeft === 0 ? 'pointer' : 'default',
              '&:hover': timeLeft === 0 ? {} : { color: '#5a32a3' },
              '&:disabled': { opacity: 0.7 },
            }}
          >
            {timeLeft === 0
              ? 'Resend code'
              : `Resend code in ${String(Math.floor(timeLeft / 60)).padStart(2, '0')}:${String(timeLeft % 60).padStart(2, '0')} sec`}
          </Typography>
          <Button
            type="button"
            fullWidth
            variant="outlined"
            sx={{
              mt: 1.5,
              py: 1,
              borderRadius: 2,
              borderColor: '#CFC4E2',
              color: '#666666',
              bgcolor: '#ffffff',
              textTransform: 'none',
              fontSize: '0.875rem',
              '&:hover': { borderColor: '#6F42C1', color: '#6F42C1', borderRadius:"1px",bgcolor: 'rgba(111,66,193,0.05) ' },
            }}
            onClick={handleClose}
            disabled={isLoading}
          >
            Back
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          maxWidth: '480px',
          borderRadius: '24px',
          position: 'relative',
          bgcolor: '#ffffff',
          border: '1px solid #CFC4E2',
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
        },
        '& .MuiDialog-container': {
          background: 'rgba(248, 240, 255, 0.6)',
        },
      }}
    >
      {verificationContent}
    </Dialog>
  );
};

export default AccountVerificationAilaunch;
